import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    addDays, addMonths, endOfMonth, endOfWeek, format, isAfter, isBefore, isSameDay, isSameMonth,
    parse, startOfDay, startOfMonth, startOfWeek, startOfYear, subDays, subMonths, subWeeks,
} from 'date-fns';
import './DateRangePicker.css';

// One control for a date range: a button showing the current range that opens
// a popover with preset ranges down the side and a two-month calendar. On the
// calendar the first click picks the start, the second picks the end (either
// order), and the range applies as soon as it's complete. Dates are exchanged
// as local YYYY-MM-DD strings, like the <input type="date"> pairs it replaces.
//
// Weeks run Sunday to Saturday. "Last 7 days" ends today; "Last 3/6 months"
// are rolling, ending today.

const ISO = 'yyyy-MM-dd';

export const toISO = (date) => format(date, ISO);

export function fromISO(value) {
    if (!value) return null;
    const d = parse(String(value), ISO, new Date());
    return Number.isNaN(d.getTime()) ? null : d;
}

export const PRESETS = [
    { key: 'last7', label: 'Last 7 days', range: (t) => [subDays(t, 6), t] },
    { key: 'thisWeek', label: 'This week', range: (t) => [startOfWeek(t), endOfWeek(t)] },
    { key: 'lastWeek', label: 'Last week', range: (t) => [startOfWeek(subWeeks(t, 1)), endOfWeek(subWeeks(t, 1))] },
    { key: 'mtd', label: 'Month to date', range: (t) => [startOfMonth(t), t] },
    { key: 'lastMonth', label: 'Last month', range: (t) => [startOfMonth(subMonths(t, 1)), endOfMonth(subMonths(t, 1))] },
    { key: 'last3', label: 'Last 3 months', range: (t) => [subMonths(t, 3), t] },
    { key: 'last6', label: 'Last 6 months', range: (t) => [subMonths(t, 6), t] },
    { key: 'ytd', label: 'Year to date', range: (t) => [startOfYear(t), t] },
];

// The preset's range as ISO strings, its end clamped to `max` when given (so
// "This week" never asks for days a page can't accept, like future ad spend).
export function presetRange(preset, max, today = startOfDay(new Date())) {
    let [start, end] = preset.range(today);
    start = startOfDay(start);
    end = startOfDay(end);
    const maxDate = fromISO(max);
    if (maxDate && isAfter(end, maxDate)) end = maxDate;
    if (isAfter(start, end)) start = end;
    return { startDate: toISO(start), endDate: toISO(end) };
}

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

// The 6 weeks of cells a month's calendar shows, Sunday-first, padded with the
// neighbouring months' days so every month is the same height.
function monthCells(month) {
    const first = startOfWeek(startOfMonth(month));
    return Array.from({ length: 42 }, (_, i) => addDays(first, i));
}

function formatRange(start, end) {
    if (!start || !end) return 'Pick dates';
    if (isSameDay(start, end)) return format(start, 'MMM d, yyyy');
    if (start.getFullYear() === end.getFullYear()) {
        return `${format(start, 'MMM d')} – ${format(end, 'MMM d, yyyy')}`;
    }
    return `${format(start, 'MMM d, yyyy')} – ${format(end, 'MMM d, yyyy')}`;
}

// Where the popover goes: under the trigger, flipped above when it wouldn't
// fit below, and kept inside the viewport horizontally. Fixed positioning
// (via a portal) lets it escape scrolling or clipped containers like modals.
function placePopover(triggerRect, popRect) {
    const gap = 6;
    const margin = 8;
    let top = triggerRect.bottom + gap;
    if (top + popRect.height > window.innerHeight - margin && triggerRect.top - gap - popRect.height >= margin) {
        top = triggerRect.top - gap - popRect.height;
    }
    let left = triggerRect.left;
    left = Math.min(left, window.innerWidth - margin - popRect.width);
    left = Math.max(margin, left);
    return { top, left };
}

export default function DateRangePicker({
    value,
    onChange,
    // Latest selectable day (YYYY-MM-DD). Later days are greyed out and presets
    // are clamped to it.
    max,
    min,
    disabled = false,
    className = '',
    id,
}) {
    const start = fromISO(value?.startDate);
    const end = fromISO(value?.endDate);
    const maxDate = fromISO(max);
    const minDate = fromISO(min);
    const today = startOfDay(new Date());

    const [open, setOpen] = useState(false);
    // The in-progress pick: set after the first click, cleared after the second.
    const [pending, setPending] = useState(null);
    const [hover, setHover] = useState(null);
    // First of the two months on show.
    const [viewMonth, setViewMonth] = useState(() => startOfMonth(end || today));
    const [pos, setPos] = useState(null);

    const triggerRef = useRef(null);
    const popRef = useRef(null);

    const activePreset = useMemo(() => {
        if (!value?.startDate || !value?.endDate) return null;
        return PRESETS.find((p) => {
            const r = presetRange(p, max, today);
            return r.startDate === value.startDate && r.endDate === value.endDate;
        }) || null;
    // today only changes at midnight; recomputing per render is fine.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value?.startDate, value?.endDate, max]);

    const openPopover = () => {
        if (disabled) return;
        // Show the picked range: its end month on the right, so the months
        // leading up to it are a click away on the left.
        setViewMonth(subMonths(startOfMonth(end || today), 1));
        setPending(null);
        setHover(null);
        setOpen(true);
    };

    const close = useCallback(() => {
        setOpen(false);
        setPending(null);
        setHover(null);
        setPos(null);
    }, []);

    // Position once rendered, then keep it put: any scroll or resize closes
    // rather than letting the popover drift away from its trigger.
    useLayoutEffect(() => {
        if (!open || !triggerRef.current || !popRef.current) return;
        setPos(placePopover(triggerRef.current.getBoundingClientRect(), popRef.current.getBoundingClientRect()));
    }, [open, viewMonth]);

    useEffect(() => {
        if (!open) return undefined;
        const onDown = (e) => {
            if (popRef.current?.contains(e.target) || triggerRef.current?.contains(e.target)) return;
            close();
        };
        const onScroll = (e) => {
            if (popRef.current?.contains(e.target)) return;
            close();
        };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('touchstart', onDown);
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', close);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('touchstart', onDown);
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', close);
        };
    }, [open, close]);

    const commit = (a, b) => {
        const [s, e] = isAfter(a, b) ? [b, a] : [a, b];
        onChange({ startDate: toISO(s), endDate: toISO(e) });
        close();
    };

    const pickPreset = (preset) => {
        onChange(presetRange(preset, max, today));
        close();
    };

    const isDisabledDay = (day) =>
        (maxDate && isAfter(day, maxDate)) || (minDate && isBefore(day, minDate));

    const pickDay = (day) => {
        if (isDisabledDay(day)) return;
        if (!pending) {
            setPending(day);
            return;
        }
        commit(pending, day);
    };

    // The range to paint: the committed one, or while picking, from the first
    // click to the day under the cursor.
    const [paintStart, paintEnd] = useMemo(() => {
        if (pending) {
            const other = hover || pending;
            return isAfter(pending, other) ? [other, pending] : [pending, other];
        }
        return [start, end];
    }, [pending, hover, start, end]);

    const inRange = (day) =>
        paintStart && paintEnd && !isBefore(day, paintStart) && !isAfter(day, paintEnd);

    // Escape closes just the popover. Stopped here so a surrounding modal's
    // document-level Escape handler doesn't close the modal too.
    const onKeyDown = (e) => {
        if (e.key === 'Escape') {
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation?.();
            close();
            triggerRef.current?.focus();
        }
    };

    const renderMonth = (month) => (
        <div className="drp__month" key={month.toISOString()}>
            <div className="drp__month-name">{format(month, 'MMMM yyyy')}</div>
            <div className="drp__grid">
                {WEEKDAYS.map((d) => <div className="drp__weekday" key={d}>{d}</div>)}
                {monthCells(month).map((day) => {
                    // Padding from the neighbouring months: blank, not a day.
                    if (!isSameMonth(day, month)) {
                        return <span key={day.toISOString()} className="drp__day drp__day--outside" aria-hidden="true" />;
                    }
                    const isStart = paintStart && isSameDay(day, paintStart);
                    const isEnd = paintEnd && isSameDay(day, paintEnd);
                    const cls = ['drp__day'];
                    if (inRange(day)) cls.push('drp__day--in');
                    if (isStart) cls.push('drp__day--start');
                    if (isEnd) cls.push('drp__day--end');
                    if (isSameDay(day, today)) cls.push('drp__day--today');
                    if (isDisabledDay(day)) cls.push('drp__day--disabled');
                    return (
                        <button
                            type="button"
                            key={day.toISOString()}
                            className={cls.join(' ')}
                            disabled={isDisabledDay(day)}
                            onClick={() => pickDay(day)}
                            onMouseEnter={() => setHover(day)}
                            onFocus={() => setHover(day)}
                            aria-label={format(day, 'EEEE, MMMM d, yyyy')}
                            aria-pressed={Boolean(isStart || isEnd)}
                        >
                            {day.getDate()}
                        </button>
                    );
                })}
            </div>
        </div>
    );

    const nextMonth = addMonths(viewMonth, 1);
    const canGoForward = !maxDate || isBefore(startOfMonth(nextMonth), startOfMonth(maxDate));

    return (
        <div className={`drp ${className}`.trim()}>
            <button
                type="button"
                id={id}
                ref={triggerRef}
                className={`drp__trigger${open ? ' drp__trigger--open' : ''}`}
                onClick={() => (open ? close() : openPopover())}
                disabled={disabled}
                aria-haspopup="dialog"
                aria-expanded={open}
            >
                <svg className="drp__icon" viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
                    <rect x="2.5" y="4" width="15" height="13" rx="2" fill="none" stroke="currentColor" strokeWidth="1.5" />
                    <path d="M2.5 8h15M6.5 2.5v3M13.5 2.5v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
                {activePreset && <span className="drp__preset-tag">{activePreset.label}</span>}
                <span className="drp__text">{formatRange(start, end)}</span>
                <span className="drp__chevron" aria-hidden="true">▾</span>
            </button>

            {open && createPortal(
                <div
                    ref={popRef}
                    className="drp__popover"
                    style={pos ? { top: pos.top, left: pos.left, visibility: 'visible' } : { top: 0, left: 0, visibility: 'hidden' }}
                    role="dialog"
                    aria-label="Choose a date range"
                    onKeyDown={onKeyDown}
                    onMouseLeave={() => setHover(null)}
                >
                    <div className="drp__presets">
                        {PRESETS.map((p) => (
                            <button
                                type="button"
                                key={p.key}
                                className={`drp__preset${activePreset?.key === p.key ? ' drp__preset--on' : ''}`}
                                onClick={() => pickPreset(p)}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>
                    <div className="drp__calendar">
                        <div className="drp__nav">
                            <button type="button" className="drp__nav-btn" onClick={() => setViewMonth((m) => subMonths(m, 1))} aria-label="Previous month">‹</button>
                            <div className="drp__hint">
                                {pending
                                    ? <>From <strong>{format(pending, 'MMM d')}</strong> — now pick the end date</>
                                    : 'Pick a start date, then an end date'}
                            </div>
                            <button type="button" className="drp__nav-btn" onClick={() => setViewMonth((m) => addMonths(m, 1))} disabled={!canGoForward} aria-label="Next month">›</button>
                        </div>
                        <div className="drp__months">
                            {renderMonth(viewMonth)}
                            {renderMonth(nextMonth)}
                        </div>
                    </div>
                </div>,
                document.body,
            )}
        </div>
    );
}
